import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Phone, Mail, Clock, Send, Loader2, MessageCircle, AlertCircle } from "lucide-react";
import { CONTACT } from "@/lib/site";
import { useLang } from "@/lib/i18n";

const WEB3FORMS_ENDPOINT = "https://api.web3forms.com/submit";
const WEB3FORMS_ACCESS_KEY = process.env.REACT_APP_WEB3FORMS_ACCESS_KEY;
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WA_BUSINESS = "15168288042"; // (516) 828-8042

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function formatPhone(value) {
  const d = value.replace(/\D/g, "").slice(0, 10);
  if (d.length === 0) return "";
  if (d.length < 4) return `(${d}`;
  if (d.length < 7) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}

export default function Contact() {
  const { t } = useLang();
  const [form, setForm] = useState({ name: "", email: "", whatsapp: "", message: "" });
  const [touched, setTouched] = useState({});
  const [loading, setLoading] = useState(false);

  const digits = form.whatsapp.replace(/\D/g, "");
  const emailValid = emailRegex.test(form.email);
  const whatsappComplete = digits.length === 10;
  const emailError = touched.email && form.email.length > 0 && !emailValid;
  const whatsappError = touched.whatsapp && digits.length > 0 && !whatsappComplete;
  const canSubmit = form.name.trim() && emailValid && whatsappComplete && form.message.trim();

  const update = (k) => (e) => {
    const value = k === "whatsapp" ? formatPhone(e.target.value) : e.target.value;
    setForm((f) => ({ ...f, [k]: value }));
  };
  const blur = (k) => () => setTouched((s) => ({ ...s, [k]: true }));

  const submit = async (e) => {
    e.preventDefault();
    setTouched({ name: true, email: true, whatsapp: true, message: true });
    if (!canSubmit) {
      toast.error(t.contact.empty);
      return;
    }
    setLoading(true);

    // Primary: Web3Forms (email delivery). Runs in the browser (client-side).
    const web3formsSubmit = (async () => {
      try {
        const formData = new FormData();
        formData.append("access_key", WEB3FORMS_ACCESS_KEY);
        formData.append("subject", "New inquiry from LocalNova Web Design website");
        formData.append("from_name", "LocalNova Web Design");
        formData.append("name", form.name);
        formData.append("email", form.email);
        formData.append("whatsapp", form.whatsapp);
        formData.append("message", form.message);
        const res = await fetch(WEB3FORMS_ENDPOINT, {
          method: "POST",
          headers: { Accept: "application/json" },
          body: formData,
        });
        const data = await res.json();
        return !!data.success;
      } catch (err) {
        return false;
      }
    })();

    // Safety net: silently save every lead to our backend (MongoDB) so nothing is lost.
    const backupSubmit = axios
      .post(`${API}/contact`, form)
      .then(() => true)
      .catch(() => false);

    try {
      const [web3ok, backupOk] = await Promise.all([web3formsSubmit, backupSubmit]);
      if (web3ok || backupOk) {
        toast.success(t.contact.success);
        setForm({ name: "", email: "", whatsapp: "", message: "" });
        setTouched({});
      } else {
        toast.error(t.contact.error);
      }
    } finally {
      setLoading(false);
    }
  };

  const sendWhatsApp = () => {
    const lines = [
      "Hi LocalNova! 👋",
      form.name && `Name: ${form.name}`,
      form.email && `Email: ${form.email}`,
      form.whatsapp && `WhatsApp: ${form.whatsapp}`,
      form.message && `\n${form.message}`,
    ].filter(Boolean);
    const text = encodeURIComponent(lines.join("\n"));
    window.open(`https://wa.me/${WA_BUSINESS}?text=${text}`, "_blank", "noopener,noreferrer");
  };

  const info = [
    { icon: Phone, label: t.contact.infoCall, value: CONTACT.phone, href: CONTACT.phoneHref, key: "call" },
    { icon: Mail, label: t.contact.infoEmail, value: CONTACT.email, href: `mailto:${CONTACT.email}`, key: "email" },
    { icon: Clock, label: t.contact.infoHours, value: t.contact.hours, href: null, key: "hours" },
  ];

  const inputBase =
    "w-full rounded-xl border bg-background px-4 py-3.5 text-foreground placeholder:text-muted-foreground/60 outline-none transition-colors duration-300";

  return (
    <section id="contact" data-testid="contact-section" className="relative py-24 md:py-32 bg-secondary/20 border-t border-border">
      <div className="mx-auto max-w-7xl px-6 md:px-10 grid lg:grid-cols-12 gap-14">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="lg:col-span-5"
        >
          <p className="font-mono-x text-xs uppercase tracking-[0.25em] text-primary mb-5">{t.contact.eyebrow}</p>
          <h2 className="font-heading font-bold tracking-tight leading-[0.95] text-4xl sm:text-5xl">
            {t.contact.heading}
          </h2>
          <p className="mt-5 text-base lg:text-lg text-muted-foreground max-w-md">
            {t.contact.paragraph}
          </p>

          <div className="mt-10 space-y-5">
            {info.map((it) => {
              const content = (
                <div className="flex items-center gap-4">
                  <span className="flex h-11 w-11 flex-none items-center justify-center rounded-xl bg-primary/10 text-primary">
                    <it.icon className="h-5 w-5" />
                  </span>
                  <div>
                    <p className="text-xs uppercase tracking-wider text-muted-foreground">{it.label}</p>
                    <p className="text-base font-medium text-foreground">{it.value}</p>
                  </div>
                </div>
              );
              return it.href ? (
                <a key={it.key} href={it.href} data-testid={`contact-info-${it.key}`} className="block hover:opacity-80 transition-opacity">
                  {content}
                </a>
              ) : (
                <div key={it.key} data-testid={`contact-info-${it.key}`}>{content}</div>
              );
            })}
          </div>
        </motion.div>

        <motion.form
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.8, delay: 0.1 }}
          onSubmit={submit}
          noValidate
          data-testid="contact-form"
          className="lg:col-span-7 rounded-2xl border border-border bg-card p-8 md:p-10"
        >
          <div className="space-y-5">
            <div>
              <label className="mb-2 block text-sm font-medium text-foreground/90">{t.contact.name}</label>
              <input
                type="text"
                value={form.name}
                onChange={update("name")}
                onBlur={blur("name")}
                data-testid="contact-name-input"
                placeholder={t.contact.namePh}
                className={`${inputBase} border-border focus:border-primary`}
              />
            </div>

            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label className="mb-2 block text-sm font-medium text-foreground/90">{t.contact.email}</label>
                <input
                  type="email"
                  value={form.email}
                  onChange={update("email")}
                  onBlur={blur("email")}
                  data-testid="contact-email-input"
                  placeholder={t.contact.emailPh}
                  aria-invalid={emailError}
                  className={`${inputBase} ${emailError ? "border-destructive focus:border-destructive" : "border-border focus:border-primary"}`}
                />
                {emailError && (
                  <p data-testid="contact-email-error" className="mt-1.5 flex items-center gap-1.5 text-xs text-destructive">
                    <AlertCircle className="h-3.5 w-3.5" /> {t.contact.invalidEmail}
                  </p>
                )}
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-foreground/90">{t.contact.whatsapp}</label>
                <input
                  type="tel"
                  inputMode="tel"
                  value={form.whatsapp}
                  onChange={update("whatsapp")}
                  onBlur={blur("whatsapp")}
                  data-testid="contact-whatsapp-input"
                  placeholder={t.contact.whatsappPh}
                  aria-invalid={whatsappError}
                  className={`${inputBase} ${whatsappError ? "border-destructive focus:border-destructive" : "border-border focus:border-primary"}`}
                />
                {whatsappError && (
                  <p data-testid="contact-whatsapp-error" className="mt-1.5 flex items-center gap-1.5 text-xs text-destructive">
                    <AlertCircle className="h-3.5 w-3.5" /> {t.contact.invalidWhatsapp}
                  </p>
                )}
              </div>
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium text-foreground/90">{t.contact.message}</label>
              <textarea
                rows={5}
                value={form.message}
                onChange={update("message")}
                onBlur={blur("message")}
                data-testid="contact-message-input"
                placeholder={t.contact.messagePh}
                className={`${inputBase} resize-none border-border focus:border-primary`}
              />
            </div>

            <div className="flex flex-col sm:flex-row items-stretch gap-3 pt-1">
              <button
                type="submit"
                disabled={loading}
                data-testid="contact-submit-button"
                className="group inline-flex flex-1 items-center justify-center gap-2 rounded-full bg-primary px-7 py-4 text-base font-semibold text-primary-foreground hover:bg-accent transition-colors duration-300 disabled:opacity-60"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" /> {t.contact.sending}
                  </>
                ) : (
                  <>
                    {t.contact.submit}
                    <Send className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5" />
                  </>
                )}
              </button>

              <span className="hidden sm:flex items-center text-xs font-mono-x uppercase tracking-wider text-muted-foreground">{t.contact.or}</span>

              <button
                type="button"
                onClick={sendWhatsApp}
                data-testid="contact-whatsapp-button"
                className="group inline-flex flex-1 items-center justify-center gap-2 rounded-full border border-primary/40 px-7 py-4 text-base font-semibold text-primary hover:bg-primary hover:text-primary-foreground transition-colors duration-300"
              >
                <MessageCircle className="h-5 w-5" />
                {t.contact.whatsappBtn}
              </button>
            </div>
          </div>
        </motion.form>
      </div>
    </section>
  );
}
